import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../utils/supabase';

interface TokenPrice {
  contract_address: string;
  chain_id: number;
  price_usd: number;
  price_24h_ago: number | null;
  price_change_24h: number | null;
  updated_at: string;
}

interface PriceData {
  price: number;
  price24hAgo: number | null;
  change24h: number | null;
}

// Chain ID constants (matching Relay API chain IDs)
const CHAIN_IDS = {
  INK: 57073,
  ETHEREUM: 1,
  MONAD: 143,
  SOLANA: 792703809, // Relay API Solana chain ID
} as const;

export { CHAIN_IDS };

/**
 * Hook for real-time token prices from Supabase cache
 * 
 * Benefits over direct Relay API calls:
 * - Instant loading (1 query vs 50+ API calls)
 * - No rate limiting
 * - Real-time updates via WebSocket
 * - 24-hour price change tracking
 * - Multi-chain support via chain_id filtering
 * 
 * @param chainId - Optional chain ID to filter prices. If not provided, fetches all chains.
 */
export const useRealtimePrices = (chainId?: number) => {
  const [prices, setPrices] = useState<Map<string, PriceData>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch initial prices
  const fetchPrices = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('token_prices')
        .select('contract_address, chain_id, price_usd, price_24h_ago, price_change_24h, updated_at');
      
      // Filter by chain_id if provided
      if (chainId !== undefined) {
        query = query.eq('chain_id', chainId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('[RealtimePrices] Fetch error:', fetchError);
        setError(fetchError.message);
        return;
      }

      if (data) {
        const priceMap = new Map<string, PriceData>();
        data.forEach((p: TokenPrice) => {
          // Use composite key: address_chainId to support same address on different chains
          const key = `${p.contract_address.toLowerCase()}_${p.chain_id}`;
          priceMap.set(key, {
            price: p.price_usd,
            price24hAgo: p.price_24h_ago,
            change24h: p.price_change_24h
          });
        });
        setPrices(priceMap);
        console.log(`[RealtimePrices] Loaded ${data.length} prices from cache${chainId ? ` (chain: ${chainId})` : ' (all chains)'}`);
      }
    } catch (err) {
      console.error('[RealtimePrices] Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [chainId]);

  useEffect(() => {
    // Initial fetch
    fetchPrices();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('token_prices_realtime')
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'token_prices' 
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object') {
            const newData = payload.new as TokenPrice;
            console.log(`[RealtimePrices] Update received for ${newData.contract_address}`);
            
            setPrices(prev => {
              const updated = new Map(prev);
              // Use composite key: address_chainId
              const key = `${newData.contract_address.toLowerCase()}_${newData.chain_id}`;
              updated.set(key, {
                price: newData.price_usd,
                price24hAgo: newData.price_24h_ago,
                change24h: newData.price_change_24h
              });
              return updated;
            });
          }
        }
      )
      .subscribe((status) => {
        console.log(`[RealtimePrices] Subscription status: ${status}`);
      });

    return () => {
      console.log('[RealtimePrices] Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [fetchPrices]);

  // Get price for a specific token (supports chain_id)
  const getPriceForToken = useCallback((address: string, tokenChainId?: number): number | null => {
    const searchChainId = tokenChainId ?? chainId ?? CHAIN_IDS.INK; // Default to Ink if not specified
    const key = `${address.toLowerCase()}_${searchChainId}`;
    const data = prices.get(key);
    return data?.price ?? null;
  }, [prices, chainId]);

  // Get 24h change for a specific token (supports chain_id)
  const get24hChangeForToken = useCallback((address: string, tokenChainId?: number): number | null => {
    const searchChainId = tokenChainId ?? chainId ?? CHAIN_IDS.INK; // Default to Ink if not specified
    const key = `${address.toLowerCase()}_${searchChainId}`;
    const data = prices.get(key);
    return data?.change24h ?? null;
  }, [prices, chainId]);

  // Get full price data for a token (supports chain_id)
  const getPriceDataForToken = useCallback((address: string, tokenChainId?: number): PriceData | null => {
    const searchChainId = tokenChainId ?? chainId ?? CHAIN_IDS.INK; // Default to Ink if not specified
    const key = `${address.toLowerCase()}_${searchChainId}`;
    return prices.get(key) ?? null;
  }, [prices, chainId]);

  return {
    prices,
    loading,
    error,
    getPriceForToken,
    get24hChangeForToken,
    getPriceDataForToken,
    refetch: fetchPrices
  };
};

/**
 * Format price change as a percentage string with + or - prefix
 */
export const formatPriceChange = (change: number | null): string => {
  if (change === null || change === undefined) return '--';
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
};

/**
 * Get color class based on price change
 */
export const getPriceChangeColor = (change: number | null): string => {
  if (change === null || change === undefined) return 'rgba(255, 255, 255, 0.5)';
  return change >= 0 ? '#10b981' : '#ef4444';
};

